import { VideoEmbed } from '@/components/common/VideoEmbed'
import { DATEFORMAT_FULL, DATEFORMAT_SHORT } from '@/defs/date'
import { type WPPostPreviewType } from '@/defs/wordpress'
import { cn } from '@/utils/commonUtil'
import { formatDate } from '@/utils/formatUtil'

import { ArticleAuthorLink } from '../Article/ArticleAuthorLink/ArticleAuthorLink'

import styles from './ArticlePreview.module.scss'
import { ArticlePreviewLink } from './ArticlePreviewLink/ArticlePreviewLink'

type LayoutType = 'horizontal' | 'vertical'

type Props = {
  article: WPPostPreviewType
  authorNoLink?: boolean
  hideExcerpt?: boolean
  isAboveFold?: boolean
  layout?: LayoutType
  layoutLg?: LayoutType
  layoutMd?: LayoutType
  layoutSm?: LayoutType
  layoutXl?: LayoutType
  newTab?: boolean
  showBg?: boolean
  showCategory?: boolean
  size?: 'lg'
}

export const ArticlePreview = ({
  article,
  authorNoLink,
  hideExcerpt,
  isAboveFold,
  layout = 'horizontal',
  layoutLg,
  layoutMd,
  layoutSm,
  layoutXl,
  newTab,
  showBg,
  showCategory,
  size,
}: Props) => {
  const { author, category, date, excerpt, featuredImageUrl, title, youtubeId } = article

  const isPodcast = category?.slug === 'podcast'
  const showExcerpt = !hideExcerpt && !!excerpt && !isPodcast

  const srcLarge = featuredImageUrl?.large
  const srcThumb = featuredImageUrl?.medium || srcLarge
  const imageLoading = isAboveFold ? 'eager' : 'lazy'
  const imagePriority = isAboveFold ? 'high' : undefined

  return (
    <div className={styles.container}>
      <div
        className={cn(styles.inner, {
          hideExcerpt,
          layout,
          layoutLg,
          layoutMd,
          layoutSm,
          layoutXl,
          showBg,
          size,
          styles,
        })}
      >
        <div className={styles.media}>
          {srcThumb && (
            <div className={styles.mediaThumb}>
              <img alt='' decoding='async' fetchPriority={imagePriority} loading={imageLoading} src={srcThumb} />
            </div>
          )}
          {youtubeId ? (
            <div className={styles.mediaVideo}>
              <VideoEmbed id={youtubeId} title={title || ''} variant='youtube' />
            </div>
          ) : (
            srcLarge && (
              <div className={styles.mediaFull}>
                <img alt='' decoding='async' fetchPriority={imagePriority} loading={imageLoading} src={srcLarge} />
              </div>
            )
          )}
          {category && showCategory && !youtubeId && <div className={styles.category}>{category.label}</div>}
        </div>
        <ArticlePreviewLink article={article} className={styles.title} newTab={newTab}>
          <span dangerouslySetInnerHTML={{ __html: title }} />
        </ArticlePreviewLink>
        <div className={styles.byline}>
          {!isPodcast && author && (
            <>
              <ArticleAuthorLink
                className={styles.bylineAuthor}
                disabled={authorNoLink}
                newTab={newTab}
                slug={author.slug}
              >
                {author.avatarUrl && (
                  <span className={styles.bylineAvatar}>
                    <img alt={`${author.name} Photo`} decoding='async' loading='lazy' src={author.avatarUrl} />
                  </span>
                )}
                <span className={styles.bylineAuthorName}>{author.name || 'EDHREC'}</span>
              </ArticleAuthorLink>
              <span className={styles.bylineDivider} />
            </>
          )}
          {date && (
            <p className={styles.bylineDate}>
              <span className={styles.bylineDateFull}>{formatDate(date, DATEFORMAT_FULL)}</span>
              <span className={styles.bylineDateShort}>{formatDate(date, DATEFORMAT_SHORT)}</span>
            </p>
          )}
        </div>
        {showExcerpt && <p className={styles.excerpt} dangerouslySetInnerHTML={{ __html: excerpt }} />}
      </div>
    </div>
  )
}
